import { useCallback, useEffect, useRef } from 'react';
import { getRectOfNodes, getViewportForBounds } from 'reactflow';

export const FIT_OPTIONS = { padding: 0.3, maxZoom: 1, minZoom: 0.25 };
const PADDING = 0.2;
// Room under the last node for its "+" and the Add step button.
const BOTTOM_ROOM = 64;

/** Fit every node in the wrapper, leaving room below for the "+" and Add step. */
export function fitGraph(rf, el, duration = 200) {
  const nodes = rf.getNodes();
  if (!nodes.length || !el) return;
  const { width, height } = el.getBoundingClientRect();
  if (!width || !height) return;
  const rect = getRectOfNodes(nodes);
  const view = getViewportForBounds({ ...rect, height: rect.height + BOTTOM_ROOM }, width, height, FIT_OPTIONS.minZoom, FIT_OPTIONS.maxZoom, PADDING);
  rf.setViewport(view, { duration });
}

/**
 * Fit the whole graph in view when its shape changes and when the room it has
 * changes (a panel opening beside it). Watches the wrapper with a
 * ResizeObserver instead of guessing how long a panel takes to settle.
 *
 * Once the person pans or zooms, resizes leave the view alone until the graph
 * changes again.
 *
 * @param {{fitView: Function}} rf  from useReactFlow()
 * @param {React.RefObject<HTMLElement>} wrapRef
 * @param {string} key  changes when the graph's shape does
 * @returns {{markUserMoved: () => void, fit: () => void}}
 */
export default function useFitOnChange(rf, wrapRef, key) {
  const movedRef = useRef(false);
  const frameRef = useRef(0);

  const fit = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    // Two frames: the first lets React Flow measure new nodes, the second fits them.
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = requestAnimationFrame(() => {
        fitGraph(rf, wrapRef.current);
      });
    });
  }, [rf, wrapRef]);

  useEffect(() => {
    movedRef.current = false;
    fit();
    return () => cancelAnimationFrame(frameRef.current);
  }, [key, fit]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let first = true;
    const ro = new ResizeObserver(() => {
      if (first) { first = false; return; } // the initial observation is not a change
      if (!movedRef.current) fit();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [wrapRef, fit]);

  const markUserMoved = useCallback(() => { movedRef.current = true; }, []);
  return { markUserMoved, fit };
}
